"use client";

import { useRef, useState } from "react";
import ReactPlayer from "react-player";
import Text from "../Text";
import { transformText } from "@/utils/transformText";
import GradientLightText from "../GradientLightText";
import NextPrevIcon from "@/public/assets/images/previous.svg";
import PlayIcon from "@/public/assets/images/play-circle.svg";
import PauseIcon from "@/public/assets/images/pause-circle.svg";
import { useSwiper } from "swiper/react";
import { TimeOfAnswerType } from "@/sections/CourseProgram";
import Button from "../Button";
import { useWindowSize } from "@/hooks/useWindowSize";
import { useTranslations } from "next-intl";

type PlayerProps = {
  name: string;
  video: string;
  thumbnail: string;
  jumpTo?: TimeOfAnswerType[];
  isSwiper?: boolean;
  jumpToText?: string;
};

const Player = ({
  name,
  video,
  thumbnail,
  jumpTo,
  isSwiper,
  jumpToText,
}: PlayerProps) => {
  const swiper = useSwiper();
  const videoRef = useRef<any>(null);
  const questionsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isJumpToModalTriggered, setIsJumpToModalTriggered] = useState(false);
  const {width} = useWindowSize();

  const t = useTranslations("common");

  const secondsToHMS = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    const formattedHours = hours.toString().padStart(2, "0");
    const formattedMinutes = minutes.toString().padStart(2, "0");
    const formattedSeconds = remainingSeconds.toString().padStart(2, "0");

    return `${
      hours > 0 ? `${formattedHours}:` : ""
    }${formattedMinutes}:${formattedSeconds}`.trim();
  };

  const handleSeekTo = (seconds: number) => {
    setIsPlaying(true);
    videoRef.current.seekTo(seconds, "seconds");
  };

  const updateActiveIndex = (currentTime: number) => {
    if (!jumpTo) {
      return;
    }
    let newIndex = -1;

    for (let i = 0; i < jumpTo.length; i++) {
      if (
        currentTime >= jumpTo[i].seconds &&
        (i + 1 === jumpTo.length || currentTime < jumpTo[i + 1].seconds)
      ) {
        newIndex = i;
        break;
      }
    }

    const activeQuestionRef = questionsRef.current[newIndex];

    if (activeIndex !== -1 && activeQuestionRef) {
      activeQuestionRef.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }

    setActiveIndex(newIndex);
  };

  return (
    <>
      <div className={`player ${!jumpTo ? "player--no-jump" : ""}`}>
        <ReactPlayer
          ref={videoRef}
          url={video}
          controls={false}
          onDuration={(duration) => setDuration(duration)}
          onPlay={() => {
            setIsPlaying(true);
          }}
          onPause={() => {
            setIsPlaying(false);
          }}
          progressInterval={1000}
          onProgress={(progress) => {
            setPlayedSeconds(progress.playedSeconds);
            if (jumpTo && jumpTo.length > 0) {
              updateActiveIndex(progress.playedSeconds);
            }
          }}
          playing={isPlaying}
          config={{
            file: {
              attributes: {
                controlsList: "nodownload",
                onContextMenu: (e: any) => e.preventDefault(),
                poster: thumbnail,
              },
            },
          }}
        />

        <div className="player__content">
          <Text element="p" elementStyle="body">
            {transformText(name, {
              gradient: (chunk) => (
                <GradientLightText element="span">{chunk}</GradientLightText>
              ),
            })}
          </Text>
          <div className="player__controls">
            {isSwiper ? (
              <button
                className="button"
                onClick={() => {
                  swiper.slidePrev();
                  setIsPlaying(false);
                }}
              >
                <NextPrevIcon />
              </button>
            ) : null}
            <>
              {isPlaying ? (
                <button className="button" onClick={() => setIsPlaying(false)}>
                  <PauseIcon />
                </button>
              ) : (
                <button className="button" onClick={() => setIsPlaying(true)}>
                  <PlayIcon />
                </button>
              )}
            </>
            {isSwiper ? (
              <button
                className="button button--rotate-180"
                onClick={() => {
                  swiper.slideNext();
                  setIsPlaying(false);
                }}
              >
                <NextPrevIcon />
              </button>
            ) : null}
          </div>
          <Text
            element="p"
            elementStyle="subtitle-secondary"
            className="weight-400 player__duration"
          >
            {secondsToHMS(playedSeconds) + "/" + secondsToHMS(duration)}
          </Text>
        </div>
      </div>
      {jumpTo && jumpTo.length > 0 ? (
        <div
          className={"player__jump-to"
            .concat(isJumpToModalTriggered ? " player__jump-to--active" : "")
            .trim()}
        >
          {width < 993 ? (
            <Button type="primary" onClick={() => setIsJumpToModalTriggered(false)}>{t("close")}</Button>
          ) : null}
          {jumpTo.map((time, index) => (
            <button
              className={"player__jump-to-button"
                .concat(
                  activeIndex === index ? " player__jump-to-button--active" : ""
                )
                .trim()}
              key={time.title}
              ref={(el) => (questionsRef.current[index] = el)}
              onClick={() => {
                handleSeekTo(time.seconds);
                setIsJumpToModalTriggered(false);
              }}
            >
              <span></span>
              <Text
                element="span"
                elementStyle="subtitle-secondary"
                className="weight-400 darker-gold player__jump-to-title"
              >
                {time.title}
              </Text>
              <Text
                element="span"
                elementStyle="body-secondary"
                className="darker-gold"
              >
                {secondsToHMS(time.seconds)}
              </Text>
            </button>
          ))}
        </div>
      ) : null}
      {jumpTo && jumpTo.length > 0 && jumpToText ? (
        <div className="player__jump-to-modal-button">
          <Button
            type="primary"
            onClick={() => setIsJumpToModalTriggered(true)}
          >
            {jumpToText}
          </Button>
        </div>
      ) : null}
    </>
  );
};

export default Player;
